import { useState, useCallback } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CreateEventDialog } from "@/components/events/CreateEventDialog";
import { EventFilters } from "@/components/events/EventFilters";
import { EventTable } from "@/components/events/EventTable";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAllEvents, useDeleteEventsBulk, useUpdateEventStatus } from "@/hooks/useEvents";
import { useCategories } from "@/hooks/useCategories";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { toast } from "sonner";
import { Check, X, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

export default function AllEventsPage() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [hidePast, setHidePast] = useState(true);
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const cursor = cursorStack.at(-1);
  const {
    data: result,
    isLoading,
    error,
  } = useAllEvents({
    status,
    search,
    categoryId: categoryId === "all" ? undefined : categoryId,
    hidePast,
    cursor,
    limit,
  });

  const events = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const { data: categories = [] } = useCategories();
  const deleteBulk = useDeleteEventsBulk();
  const updateStatus = useUpdateEventStatus();

  const toggleAll = () => {
    if (selected.size === events.length) setSelected(new Set());
    else setSelected(new Set(events.map((e) => e.id)));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    try {
      await deleteBulk.mutateAsync(ids);
      setSelected(new Set());
      toast.success(`Usunięto ${ids.length} wydarzeń`);
    } catch (err) {
      toast.error("Nie udało się usunąć wydarzeń", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleBulkStatus = async (status: "approved" | "rejected") => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => updateStatus.mutateAsync({ id, status })));
      setSelected(new Set());
      toast.success(
        status === "approved" ? `Zatwierdzono ${ids.length}` : `Odrzucono ${ids.length}`,
      );
    } catch (err) {
      toast.error("Operacja nie powiodła się", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const bulkBusy = deleteBulk.isPending || updateStatus.isPending;

  const handleStatusChange = (v: string) => {
    setStatus(v);
    setCursorStack([]);
  };
  const handleCategoryChange = (v: string) => {
    setCategoryId(v);
    setCursorStack([]);
  };
  const handleHidePastChange = (v: boolean) => {
    setHidePast(v);
    setCursorStack([]);
  };
  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));

  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader title="Wszystkie wydarzenia" action={<CreateEventDialog />} />
      <EventFilters
        search={search}
        onSearchChange={handleSearchChange}
        status={status}
        onStatusChange={handleStatusChange}
        categoryId={categoryId}
        onCategoryChange={handleCategoryChange}
        categories={categories}
        hidePast={hidePast}
        onHidePastChange={handleHidePastChange}
      />

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white animate-pulse border border-black/5" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania: {error.message}</p>}
      {!isLoading && events.length === 0 && (
        <p className="text-center py-12 text-muted-foreground">
          Brak wydarzeń spełniających kryteria.
        </p>
      )}
      {!isLoading && events.length > 0 && (
        <EventTable
          events={events}
          categories={categories}
          selected={selected}
          onToggleAll={toggleAll}
          onToggleOne={toggleOne}
        />
      )}

      {!isLoading && (events.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
        <button
          type="button"
          className="bulk-action-btn"
          disabled={bulkBusy}
          onClick={() => handleBulkStatus("approved")}
        >
          <Check className="h-3.5 w-3.5" strokeWidth={3} /> Zatwierdź
        </button>
        <button
          type="button"
          className="bulk-action-btn"
          disabled={bulkBusy}
          onClick={() => handleBulkStatus("rejected")}
        >
          <X className="h-3.5 w-3.5" strokeWidth={3} /> Odrzuć
        </button>
        <button
          type="button"
          className="bulk-action-btn"
          data-tone="danger"
          disabled={bulkBusy}
          onClick={handleBulkDelete}
        >
          <Trash2 className="h-3.5 w-3.5" /> Usuń zaznaczone
        </button>
      </BulkActionBar>
    </div>
  );
}
